import { message } from "antd";
import { maxBy } from "lodash";
import { observable, makeAutoObservable, action, runInAction, computed } from "mobx";
import { createContext } from "react";
import { getGeneralMomentListRequest, getGeneralMomentRequest, updateGeneralMomentRequest } from "../axios/routes/generalMoment";
import {
  analyzeMomentsRequest,
  getMomentMomentoListRequest,
  getMomentSeatPreviewAllRequest,
  deleteMomentLinkRequest,
  rotateMomentImageRequest,
  getMaskedSeatImageUrlRequest,
  getOperatorMomentRequest,
} from "../axios/routes/moment";
import { getTeamByIdRequest } from "../axios/routes/teams";
import {
  GeneralMomentClient, MomentStatus, MomentsAnalysisType, OperatorGamePopulated,
  OperatorMoment,
  PreviewSeat, UpdateMomentRequest, AnalyzeMomentsRequest,
} from "../shared/interfaces";
import { isValidNumber } from "../utils/validation";
import {
  PROHIBITED_MOMENT_TEXT_MESSAGE,
  isProhibitedMomentTextError,
} from "../utils/momentTextModeration";
import { GameServiceInstance } from "./game";
import { NotificationServiceInstance } from "./notification";
import { TemplateServiceInstance } from "./template";

function ticketRefId(value: unknown): string {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object") {
    const nestedId = (value as { _id?: unknown })._id;
    if (nestedId != null && nestedId !== "") {
      return String(nestedId);
    }
    const asString = String(value);
    if (asString && asString !== "[object Object]") {
      return asString;
    }
  }
  return "";
}

export type MomentTag = "archived"|"published"|"data"|"attention";

function momentoLinkStorageKey(link: string): string {
  try {
    const url = new URL(link);
    return url.searchParams.get("key") ?? link;
  } catch {
    return link;
  }
}

/** Dedupe wide-shot URLs that share the same underlying S3 key (legacy duplicate moment docs). */
function dedupeMomentoLinksByStorageKey(links: string[]): string[] {
  const seen = new Set<string>();
  return links.reduce<string[]>((out, link) => {
    const key = momentoLinkStorageKey(link);
    if (seen.has(key)) {
      return out;
    }
    seen.add(key);
    out.push(link);
    return out;
  }, []);
}

/** One child moment doc per camera capture (legacy duplicate rows). */
function uniqueCameraMomentsForSelectedCamera(
  moments: OperatorMoment[],
  selectedCameraName: string,
): OperatorMoment[] {
  const seenCaptureKeys: Record<string, boolean> = {};
  return moments.filter((m) => {
    if (m.camera !== selectedCameraName) {
      return false;
    }
    const captureKey = `${m.camera}|${m.momentStart ?? m._id}`;
    if (seenCaptureKeys[captureKey]) {
      return false;
    }
    seenCaptureKeys[captureKey] = true;
    return true;
  });
}

export interface GeneralMomentClientWithTag extends GeneralMomentClient {
  tag?: MomentTag;
}

export const getCamerasList = (moment: GeneralMomentClientWithTag): string[] => {
  if (!moment || !moment.moments || !moment.moments.length) {
    return [];
  }

  const existingCameras = {};
  const list = moment.moments.reduce((acc, m) => {
    const { camera } = m;
    if (!existingCameras[camera]) {
      existingCameras[camera] = true;
      acc.push(camera);
    }

    return acc;
  }, []);

  return list.sort((a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  });
};

export const getCamerasCount = (moment: GeneralMomentClientWithTag): number => {
  const list = getCamerasList(moment);

  return list.length;
};

export function collectUsedDataMomentTexts(
  momentList: { moments?: { status?: string; text?: string }[] }[],
): Set<string> {
  const used = new Set<string>();
  momentList.forEach((gm) => {
    (gm.moments || []).forEach((meta) => {
      if (meta.status !== MomentStatus.DATA) return;
      const t = (meta.text || "").trim();
      if (t) used.add(t);
    });
  });
  return used;
}

class MomentService {
  @observable momentList: GeneralMomentClient[] = [];

  @observable momentoLinksLoading = false;

  @observable momentoLinks: string[] = [];

  @observable momentoPreviewLinks: string[] = [];

  @observable momentoFullSizeLinks: string[] = [];

  @observable selectedMoment: GeneralMomentClientWithTag = null;

  @observable selectedMomentCameraName: string = null;

  @observable momentViewerOpened = false;

  @observable momentGameData: OperatorGamePopulated = null;

  @observable previewTitle: string = null;

  @observable previewSeats: string[] = [];

  @observable seatPreviewLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Searches for the max score among published moments
   */
  @computed get latestPublishedMoment() {
    const publishedMoments = this.momentList.filter((m) => m.moments[0].status === "published");
    if (publishedMoments.length) {
      return maxBy(publishedMoments, (m) => m.startTime).moments[0];
    }
    return null;
  }

  /**
   * Cameras that operator can select from dropdown to preview image from specific camera
   */
  @computed get cameras() {
    return getCamerasList(this.selectedMoment);
  }

  public closeMomentViewer = () => { this.momentViewerOpened = false; }

  public openMomentViewer = () => { this.momentViewerOpened = true; }

  public selectMoment = (gMoment: GeneralMomentClient, cameraName?: string): void => {
    this.selectedMoment = gMoment;
    this.selectedMomentCameraName = cameraName;
    if (!cameraName) {
      if (gMoment?.moments?.length > 0) {
        this.selectedMomentCameraName = gMoment.moments[0].camera;
      }
    }

    this.fetchAllMomento();

    this.openMomentViewer();
  }

  public selectCamera = (cameraName: string): void => {
    this.selectedMomentCameraName = cameraName || null;
    this.fetchAllMomento();
  }

  public deleteImageFromMoment = async (link: string, index: number) : Promise<boolean> => {
    let itemRemoved = false;
    for (let ind = 0, len = this.selectedMoment.moments.length; ind < len; ind += 1) {
      const m = this.selectedMoment.moments[ind];
      if (m.camera === this.selectedMomentCameraName) {
        // eslint-disable-next-line no-await-in-loop
        const momentoListResponse = await deleteMomentLinkRequest(m._id, link);
        if (("itemRemoved" in momentoListResponse) && (momentoListResponse.itemRemoved === true)) {
          itemRemoved = true;
        }
      }
    }
    return itemRemoved;
  }

  public rotateImageFromMoment = async (
    link: string,
    viewerEmail: string,
  ): Promise<boolean> => {
    const cameraMoments = uniqueCameraMomentsForSelectedCamera(
      this.selectedMoment?.moments || [],
      this.selectedMomentCameraName || "",
    );
    const momentId = cameraMoments[0]?._id;
    if (!momentId) {
      return false;
    }
    const response = await rotateMomentImageRequest(momentId, link, viewerEmail);
    if (!response.rotated) {
      return false;
    }
    await this.fetchAllMomento();
    return true;
  }

  @action public clearData = () => {
    this.momentList = [];

    this.momentoLinks = [];

    this.selectedMoment = null;
    this.selectedMomentCameraName = null;

    this.momentGameData = null;

    this.momentoLinksLoading = false;

    this.previewSeats = [];
    this.previewTitle = null;
  }

  public fetchMomentList = async (gameId: string) => {
    try {
      await this.fetchGameData(gameId);

      const response = await getGeneralMomentListRequest({
        from: this.momentGameData.startDate,
        to: this.momentGameData.finishDate || null,
        stadium: this.momentGameData.stadium._id,
        gameId,
      });

      console.info(response);

      runInAction(() => {
        this.momentList = response;
      });
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchMomentList()", err.message);
    }
  }

  public clearMomentList = async () => {
    runInAction(() => {
      this.momentList = [];
    });
  }

  private fetchGameData = async (gameId: string) => {
    try {
      let gameData = await GameServiceInstance.fetchGameById(gameId);
      if (gameData) {
        const team1 = gameData.team1 as {
          _id?: string;
          hostname?: string;
          defaultTicketTemplate?: unknown;
        } | undefined;
        const team1Id = team1?._id ? String(team1._id) : "";
        const hasDefaultTicket = !!(
          ticketRefId(gameData.ticketTemplate)
          || ticketRefId(gameData.template)
          || ticketRefId(team1?.defaultTicketTemplate)
        );
        if (team1Id && !hasDefaultTicket) {
          try {
            const team = await getTeamByIdRequest(team1Id);
            if (team?.defaultTicketTemplate) {
              gameData = {
                ...gameData,
                team1: {
                  ...gameData.team1,
                  defaultTicketTemplate: String(team.defaultTicketTemplate),
                },
              };
            }
          } catch (err) {
            console.warn("fetchGameData() team default ticket lookup failed:", err);
          }
        }
        this.momentGameData = gameData;
        if (team1Id) {
          await TemplateServiceInstance.fetchTemplatesForTeam(
            team1Id,
            team1?.hostname,
          );
        } else {
          await TemplateServiceInstance.fetchTemplates();
        }
      }
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchGameData()", err.message);
    }
  }

  public getOperatorMoment = async (_id: string) => {
    const response = await getOperatorMomentRequest(_id);
    return response;
  }

  private fetchMomentMomento = async (_id: string) => {
    const response = await getMomentMomentoListRequest(_id);
    return response;
  }

  private fetchAllMomento = async () => {
    this.momentoLinksLoading = true;
    try {
      let previewLinks: string[] = [];
      let fullSizeLinks: string[] = [];
      const cameraMoments = uniqueCameraMomentsForSelectedCamera(
        this.selectedMoment.moments,
        this.selectedMomentCameraName,
      );
      for (let ind = 0, len = cameraMoments.length; ind < len; ind += 1) {
        const m = cameraMoments[ind];
        // eslint-disable-next-line no-await-in-loop
        const momentoListResponse = await this.fetchMomentMomento(m._id);
        previewLinks = [...previewLinks, ...momentoListResponse.momentOLinks];
        const untypedResponse : any = momentoListResponse;
        if ("fullSizeLinks" in untypedResponse) {
          fullSizeLinks = [...fullSizeLinks, ...untypedResponse.fullSizeLinks];
        }
      }

      previewLinks = dedupeMomentoLinksByStorageKey(previewLinks);
      fullSizeLinks = dedupeMomentoLinksByStorageKey(fullSizeLinks);

      this.momentoPreviewLinks = previewLinks.map((link) => {
        // Find the width and height parameters in the links and divide by 10
        const url = new URL(link);
        // const width = parseInt(url.searchParams.get("width"), 10);
        // const height = parseInt(url.searchParams.get("height"), 10);
        url.searchParams.set("width", "600"); // `${width / 10}`
        url.searchParams.set("height", "600"); // `${height / 10}`
        return url.href;
      });

      this.momentoLinks = previewLinks;
      this.momentoFullSizeLinks = fullSizeLinks;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchAllMomento()", err.message);
    } finally {
      this.momentoLinksLoading = false;
    }
  }

  private fetchMomentMomentoForSeatPreview = async (seat: PreviewSeat, _id: string) => {
    const response = await getMomentSeatPreviewAllRequest(
      seat,
      _id,
    );

    return {
      mLinks: response.momentOLinks,
      prSeat: response.prSeat,
    };
  }

  public getSeatPreview = async (seat?: PreviewSeat): Promise<void> => {
    this.seatPreviewLoading = true;
    try {
      let links: string[] = [];
      let previewSeat = seat;
      const cameraMoments = uniqueCameraMomentsForSelectedCamera(
        this.selectedMoment.moments,
        this.selectedMomentCameraName,
      );
      for (let ind = 0, len = cameraMoments.length; ind < len; ind += 1) {
        const m = cameraMoments[ind];
        const {
          mLinks,
          prSeat,
        // eslint-disable-next-line no-await-in-loop
        } = await this.fetchMomentMomentoForSeatPreview(previewSeat, m._id);

        if (!previewSeat) {
          previewSeat = prSeat;
        }

        links = [...links, ...mLinks];
      }

      this.previewSeats = dedupeMomentoLinksByStorageKey(links);
      const { row: r, seat: s, sectionName } = previewSeat;
      this.previewTitle = `Preview (section ${sectionName} row ${r} seat ${s})`;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("getSeatPreview()", err.message);
    } finally {
      this.seatPreviewLoading = false;
    }
  }

  public clearSeatPreview = () => {
    this.previewSeats = [];
  }

  public updateMoment = async (
    request: UpdateMomentRequest,
    _id: string,
    tag?: MomentTag,
  ): Promise<boolean> => {
    try {
      const prev = this.momentList.find((m) => m._id === _id)?.moments?.[0];
      const prevStatus = prev?.status;
      const prevText = (prev?.text || "").trim();

      if (await updateGeneralMomentRequest(request, _id)) {
        const updatedMoment = await getGeneralMomentRequest(_id);
        let applied = false;
        runInAction(() => {
          const index = this.momentList.findIndex((m) => m._id === _id);
          if (index < 0) return;

          const updatedMomentWithTag: GeneralMomentClientWithTag = {
            ...updatedMoment,
            tag,
          };
          const next = this.momentList.slice();
          next[index] = updatedMomentWithTag;
          this.momentList = next;
          if (this.selectedMoment && this.selectedMoment._id === _id) {
            this.selectedMoment = updatedMomentWithTag;
          }
          applied = true;
        });
        if (!applied) return false;

        const next = this.momentList.find((m) => m._id === _id)?.moments?.[0];
        if (next) {
          this.maybeRunAutomatedDataAnalytics(_id, {
            prevStatus,
            prevText,
            nextStatus: next.status,
            nextText: (next.text || "").trim(),
          }).catch(() => undefined);
        }

        return true;
      }
      return false;
    } catch (err) {
      const errMessage = err?.message || "";
      if (isProhibitedMomentTextError(errMessage)) {
        message.error(PROHIBITED_MOMENT_TEXT_MESSAGE);
      } else {
        NotificationServiceInstance.showUnexpectedErrorNotification("updateMoment()", errMessage);
      }
      return false;
    }
  }

  /**
   * Fires Crowd Analytics for a Data moment once the operator has set a caption
   * from the team's configured dropdown. Triggered on the *transition* into the
   * "Data + valid caption" state to avoid re-running on subsequent edits.
   */
  private maybeRunAutomatedDataAnalytics = async (
    momentId: string,
    args: {
      prevStatus?: MomentStatus;
      prevText: string;
      nextStatus?: MomentStatus;
      nextText: string;
    },
  ): Promise<void> => {
    const { prevStatus, prevText, nextStatus, nextText } = args;
    if (nextStatus !== MomentStatus.DATA) return;

    const team1 = this.momentGameData?.team1;
    const automatedEnabled = team1?.automatedDataRuns === true;
    const crowdHidden = String(team1?.name || "").trim().toLowerCase() === "chicago cubs";
    if (!automatedEnabled || crowdHidden) return;

    const captions = (team1?.dataMomentCaptions || [])
      .map((c) => String(c).trim())
      .filter(Boolean);
    if (captions.length === 0) return;
    const captionSet = new Set(captions);
    if (!nextText || !captionSet.has(nextText)) return;

    const wasAlreadyValid = prevStatus === MomentStatus.DATA
      && !!prevText
      && captionSet.has(prevText);
    if (wasAlreadyValid) return;

    this.onUseInAnalyze(momentId, true);

    try {
      NotificationServiceInstance.showInfoNotification(
        "Running Crowd Analytics for Data moment...",
      );
      const result = await this.analyzeMoments({
        moments: [momentId],
        analysisType: MomentsAnalysisType.CROWD_ANALYTICS,
        gameId: this.momentGameData?._id,
      });
      if (result?.success) {
        NotificationServiceInstance.showSuccessNotification(
          `Crowd Analytics complete: ${result.totalNumberOfImages} section images generated`,
        );
      }
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification(
        "maybeRunAutomatedDataAnalytics()",
        (err as Error).message,
      );
    }
  }

  @action public analyzeMoments = async (request: AnalyzeMomentsRequest)
  : Promise<{ success: boolean,
              totalNumberOfMoments: number,
              totalNumberOfImages: number}|null> => {
    try {
      return await analyzeMomentsRequest(request);
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("analyzeMoments()", err.message);
      return null;
    }
  }

  @action public getMaskedSeatImageUrl = async (
    cameraId: string,
    sectionId: string,
    rowId:string,
    seatId: string,
  )
  : Promise<{success: boolean, croppedImageURL:string|null, maskSVGKey:string|undefined}|null> => {
    try {
      return await getMaskedSeatImageUrlRequest(cameraId, sectionId, rowId, seatId);
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("getMaskedSeatImageUrl()", err.message);
      return null;
    }
  }

  @action public publishMoment = (
    momentId: string,
    sportName?: string,
    sitStand?: "sitting" | "standing",
  ) => {
    const targetGMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetGMoment) return;

    let canPublish = true;
    const isOtherSport = sportName && sportName.startsWith("Other");
    if (!targetGMoment.moments[0].text) {
      message.warn("Please specify the description to publish the moment");
      canPublish = false;
    }
    if (!isValidNumber(targetGMoment.moments[0].team1Score) && !isOtherSport) {
      message.warn("Please specify the team 1 score to publish the moment");
      canPublish = false;
    }
    if (!isValidNumber(targetGMoment.moments[0].team2Score) && !isOtherSport) {
      message.warn("Please specify the team 2 score to publish the moment");
      canPublish = false;
    }

    const sitStandRequired = !!this.momentGameData?.team1?.sitStandShiftingEnabled;
    const resolvedSitStand = sitStand
      || (targetGMoment.moments[0].sitStand === "sitting"
        || targetGMoment.moments[0].sitStand === "standing"
        ? targetGMoment.moments[0].sitStand
        : undefined);
    if (sitStandRequired && resolvedSitStand !== "sitting" && resolvedSitStand !== "standing") {
      message.error("Select Sitting or Standing Moment before publishing");
      canPublish = false;
    }

    if (canPublish) {
      this.updateMoment({
        status: MomentStatus.PUBLISHED,
        ...(resolvedSitStand ? { sitStand: resolvedSitStand } : {}),
      }, momentId, "published");
    }
  }

  @action public archiveMoment = (momentId: string) => {
    const targetMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetMoment) return;

    this.updateMoment({ status: MomentStatus.ARCHIVED }, momentId, "archived");
  }

  @action public dataMoment = async (momentId: string) => {
    const targetMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetMoment) return;

    // Just flip status to Data. Automated Crowd Analytics is fired from
    // `updateMoment` once the operator picks a caption from the team's
    // dropdown — Data alone is not enough.
    await this.updateMoment(
      { status: MomentStatus.DATA },
      momentId,
      "data",
    );
  }

  @action public attentionTrackingMoment = async (momentId: string) => {
    const targetMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetMoment) return;

    await this.updateMoment(
      { status: MomentStatus.ATTENTION_TRACKING },
      momentId,
      "attention",
    );
  }

  @action public setIsMOTGMoment = async (momentId: string, checked: boolean) => {
    const targetMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetMoment) return;

    // Update the moment's MOTG status
    const updateSuccess = await this.updateMoment({ isMOTG: checked }, momentId);

    // If checking the MOTG checkbox, trigger section image generation
    if (checked && updateSuccess) {
      let progressIntervalId: ReturnType<typeof setInterval> | null = null;

      try {
        NotificationServiceInstance.showInfoNotification(
          "Generating section images for Moment of the Game...",
        );

        // Start polling for progress immediately
        const { getAnalyzeProgressRequest } = await import("../axios/routes/moment");

        progressIntervalId = setInterval(async () => {
          try {
            const progressResponse = await getAnalyzeProgressRequest([momentId]);

            if (progressResponse.success && progressResponse.progress) {
              const {
                stage,
                totalImagesGenerated,
                totalImagesExpected,
                completed,
              } = progressResponse.progress;

              // Show stage-appropriate progress notification
              let progressMessage: string;
              if (stage === "calculating") {
                progressMessage = "Calculating section geometry...";
              } else if (stage === "generating") {
                progressMessage = (
                  `Generating section images: ${totalImagesGenerated} / ${totalImagesExpected}`
                );
              } else {
                progressMessage = `Complete: ${totalImagesGenerated} images generated`;
              }

              NotificationServiceInstance.showInfoNotification(progressMessage, 0);

              // Stop polling if completed
              if (completed && progressIntervalId) {
                clearInterval(progressIntervalId);
                progressIntervalId = null;
              }
            }
          } catch (progressErr) {
            console.error("Error polling progress:", progressErr);
          }
        }, 2000); // Poll every 2 seconds

        const result = await this.analyzeMoments({
          moments: [momentId],
          analysisType: MomentsAnalysisType.PREGENERATE_SECTION_IMAGES,
        });

        // Stop polling when analyze completes
        if (progressIntervalId) {
          clearInterval(progressIntervalId);
          progressIntervalId = null;
        }

        if (result?.success) {
          NotificationServiceInstance.showSuccessNotification(
            `Successfully generated ${result.totalNumberOfImages} section images for MOTG`,
          );
        }
      } catch (err) {
        // Clean up polling on error
        if (progressIntervalId) {
          clearInterval(progressIntervalId);
        }

        NotificationServiceInstance.showUnexpectedErrorNotification(
          "setIsMOTGMoment() - section image generation",
          err.message,
        );
      }
    }
  }

  @action public setHideScoreboardMoment = (momentId: string, checked: boolean) => {
    const targetMoment = this.momentList.find((m) => m._id === momentId);
    if (!targetMoment) return;

    this.updateMoment({ hideScoreboard: checked }, momentId);
  }

  @action public onUseInAnalyze = (momentId: string, checked: boolean) => {
    const index = this.momentList.findIndex((m) => m._id === momentId);
    if (index !== -1) {
      const moment = this.momentList[index];
      const updatedMoment = {
        ...moment,
        useInAnalyze: checked,
      };

      this.momentList.splice(index, 1, updatedMoment);

      if (this.selectedMoment && this.selectedMoment._id === momentId) {
        this.selectedMoment = updatedMoment;
      }
    }
  }

  @action public pairMoments = async (momentId1: string, momentId2: string): Promise<boolean> => {
    try {
      const { pairMomentsRequest } = await import("../axios/routes/generalMoment");
      const success = await pairMomentsRequest(momentId1, momentId2);
      if (success) {
        NotificationServiceInstance.showSuccessNotification(
          "Moments paired successfully",
        );
        const gameId = this.momentGameData?._id;
        if (gameId) {
          await this.fetchMomentList(gameId);
        }
        runInAction(() => {
          if (
            this.selectedMoment
            && !this.momentList.some((m) => m._id === this.selectedMoment._id)
          ) {
            this.selectedMoment = null;
            this.momentViewerOpened = false;
          }
        });
      }
      return success;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification(
        "pairMoments()",
        err.message,
      );
      return false;
    }
  }

  /**
   * Socket actions
   */
  @action socketAddDraftGeneralMoment = (gameId: string, moment: GeneralMomentClient): boolean => {
    if (!this.momentGameData || this.momentGameData._id !== gameId) {
      console.log("[new draft moment][moments service] moment wasn't added to the list since it's not from the selected game");
      return false;
    }

    this.momentList.unshift(moment);
    console.log("[new draft moment][moments service] moment was added to the list");
    return true;
  }

  /**
   * @returns true when this general moment is in the current game's list (toast shown here).
   */
  @action socketUpdateGeneralMoment = (
    generalMomentId: string,
    moment: OperatorMoment,
  ): boolean => {
    const targetGMomentIndex = this.momentList.findIndex((m) => m._id === generalMomentId);
    if (targetGMomentIndex === -1) {
      return false;
    }

    const targetGMoment = this.momentList[targetGMomentIndex];
    targetGMoment.moments.push(moment);

    NotificationServiceInstance.showUpdatedMomentNotification(
      targetGMoment._id,
      moment._id,
      moment.camera,
      (gMomentId, momentId) => {
        const gMoment = this.momentList.find((m) => m._id === gMomentId);
        if (!gMoment) return;

        const cameraIndex = gMoment.moments.findIndex((m) => m._id === momentId);

        if (cameraIndex !== -1) {
          const cameraName = gMoment.moments[cameraIndex].camera;
          this.selectMoment(gMoment, cameraName);
        }
      },
    );
    return true;
  }
}

export const MomentServiceInstance = new MomentService();

export const MomentServiceContext = createContext(MomentServiceInstance);
